import { useState, useCallback, useRef, useEffect } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { useCustomers, useUpdateCustomer } from "../hooks/useCustomers";
import CustomerSearch from "../components/CustomerSearch";
import AddCurrentBtn from "../../../components/AddCurrentBtn";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COLUMNS = [
  { header: "col.id", accessor: "id", type: "number" },
  { header: "col.firstName", accessor: "firstname", type: "text" },
  { header: "col.lastName", accessor: "lastname", type: "text" },
  { header: "col.phone", accessor: "phone", type: "phone" },
  { header: "col.email", accessor: "email", type: "text" },
  { header: "col.birthDate", accessor: "birthDate", type: "date" },
  { header: "col.minutesBalance", accessor: "minutesBalance", type: "number" },
  { header: "col.debt", accessor: "debt", type: "number" },
  { header: "col.registeredAt", accessor: "createdAt", type: "datetime" },
];

const FORM_FIELDS = [
  { name: "id", label: "ID", type: "number", disabled: true },
  { name: "firstname", label: "form.firstName", type: "text" },
  { name: "lastname", label: "form.lastName", type: "text", required: false },
  { name: "phone", label: "form.phone", type: "phone", disabled: true },
  { name: "email", label: "form.email", type: "text" },
  { name: "birthDate", label: "form.birthDate", type: "text", required: false },
  {
    name: "sendMarketing",
    label: "form.marketingConsent",
    type: "boolean",
    disabled: true,
  },
  { name: "minutesBalance", label: "form.minutesAmount", type: "number" },
  { name: "debt", label: "col.debt", type: "number" },
  { name: "comment", label: "form.comment", type: "text", required: true },
];

export default function CustomersPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState(null);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCustomers(searchParams?.categoryIdx, searchParams?.text);
  const updateMutation = useUpdateCustomer();
  const [editingRow, setEditingRow] = useState(null);
  const [values, setValues] = useState(null);
  const loadMoreRef = useRef(null);

  // Flatten all pages into a single array
  const rows = data?.pages?.flatMap((p) => p.items) ?? [];

  // Infinite scroll via IntersectionObserver
  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserver]);

  const openEdit = (row) => {
    setEditingRow(row);
    setValues({ ...row });
  };

  const handleSave = () => {
    if (!values) return;
    const cleaned = {
      ...values,
      birthDate: values.birthDate === "" ? null : values.birthDate,
    };
    updateMutation.mutate(cleaned, {
      onSuccess: () => {
        toast.success(t("toast.recordUpdated"));
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordUpdateFailed")),
    });
  };

  return (
    <PageShell
      title={t("page.customers")}
      actions={<CustomerSearch onResult={setSearchParams} />}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <>
          <DataTable columns={COLUMNS} data={rows} onRowClick={openEdit} />
          {/* Sentinel element for infinite scroll */}
          <div ref={loadMoreRef} className="h-4 w-full" />
          {isFetchingNextPage && (
            <p className="text-c-gray-400 text-center py-2">
              {t("common.loading")}
            </p>
          )}
        </>
      )}

      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>{t("dialog.editRecord")}</DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
            {editingRow?.id && <AddCurrentBtn id={editingRow.id} />}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
